import { useInfiniteQuery } from '@tanstack/react-query'
import { Empty, Flex, Select, SelectProps, Spin } from 'antd'
import { useMemo, useState } from 'react'

import { useTemplatesServiceGetSystemTemplateListEndpointKey } from 'src/api/queries'
import {
  ClassifierService,
  PreventionTreatmentContingentDto
} from 'src/api/requests'
import { DEFAULT_PER_PAGE } from 'src/app-constants'
import { useDebounce } from 'src/hooks'
import { useLocales } from 'src/locales'

type Props = {
  typesId?: string[]
} & SelectProps

export const TreatmentContingentTypeInfiniteSelect = ({
  typesId,
  ...props
}: Props) => {
  const { t, currentLang } = useLocales()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search)

  const { data, isFetching, fetchNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: [
      useTemplatesServiceGetSystemTemplateListEndpointKey,
      debouncedSearch,
      typesId
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response =
        await ClassifierService.searchPreventionTreatmentContingentEndpoint(
          pageParam,
          DEFAULT_PER_PAGE,
          debouncedSearch,
          undefined,
          undefined,
          typesId
        )

      return {
        data: response.data,
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data?: PreventionTreatmentContingentDto[]
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1
  })

  const selectOptions = useMemo(
    () => [
      ...(data?.pages.flatMap(
        (page) =>
          page.data?.map((item) => ({
            value: item?.id,
            label: item?.translation?.[currentLang?.value]
          })) || []
      ) || [])
    ],
    [data]
  )

  return (
    <Select
      allowClear
      popupMatchSelectWidth={true}
      filterOption={false}
      options={selectOptions}
      onSearch={(value) => setSearch(value)}
      onClear={() => setSearch('')}
      loading={isFetching}
      placeholder={t('investigations.treatment_reason')}
      showSearch={true}
      onPopupScroll={(e) => {
        const target = e.target as HTMLElement
        if (target.scrollTop + target.offsetHeight === target.scrollHeight) {
          hasNextPage && fetchNextPage()
        }
      }}
      notFoundContent={
        isFetching ? (
          <Flex justify="center">
            <Spin />
          </Flex>
        ) : (
          <Empty />
        )
      }
      {...props}
    />
  )
}
